import React, { useMemo, useState } from "react";
import {
  Gift,
  Star,
  ChevronRight,
  ShieldCheck,
  Percent,
  Trophy,
} from "lucide-react";

const CATEGORIES = ["Vouchers", "Products", "Health Checks", "Donations", "Experiences"];

const FEATURED = [
  { code: "W100", label: "₹100 OFF", desc: "On orders above ₹499", points: 300, unlimited: true },
  { code: "S250", label: "₹250 OFF", desc: "On orders above ₹999", points: 600, unlimited: true },
  { code: "FSHIP", label: "Free Shipping", desc: "On any order", points: 200, unlimited: true },
  { code: "S500", label: "₹500 OFF", desc: "On orders above ₹1499", points: 1000, unlimited: true },
];

const VOUCHERS = [
  { code: "FLAT-15", label: "Flat 15% OFF", desc: "Sitewide. Min. order ₹499", days: 30, points: 900 },
  { code: "MED-75", label: "Flat ₹75 OFF", desc: "On medicines. Min. order ₹799", days: 20, points: 250 },
  { code: "PC-20", label: "Flat 20% OFF", desc: "On personal care. Min. order ₹999", days: 30, points: 700 },
];

const REDEEM_PRODUCTS = [
  { name: "HealthOK Multivitamin Tablets", pack: "60 Tablets", points: 500 },
  { name: "AccuSure Digital Thermometer", pack: "1 Unit", points: 350 },
  { name: "Dr. Morepen BP Monitor (BP-09)", pack: "1 Unit", points: 1200 },
  { name: "Cipla Hand Sanitizer", pack: "200 ml", points: 300 },
];

export default function RewardZone() {
  const [tab, setTab] = useState("Vouchers");

  const items = useMemo(() => (tab === "Vouchers" ? VOUCHERS : []), [tab]);

  return (
    <div data-testid="account-reward-zone" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          Reward Zone
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Redeem your points, unlock exclusive rewards and enjoy special benefits.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white p-5">
          <p className="text-xs font-semibold uppercase tracking-wider opacity-90">
            Your Points Balance
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Gift className="h-6 w-6" />
            <p className="font-display text-3xl font-extrabold">2,450</p>
          </div>
          <p className="text-xs opacity-90 mt-1">₹245.00 value · 1 Point = ₹0.10</p>
          <button className="mt-4 inline-flex items-center gap-1 text-xs font-bold bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5">
            How to earn points <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-gray-100 bg-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Your Tier: Platinum
              </p>
              <p className="text-sm text-gray-600 mt-1">
                You&apos;re only <b>550 points</b> away from Diamond tier!
              </p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-[#F3EFFC] text-[#4B2AAD] text-[10px] font-bold px-2 py-0.5">
              <Trophy className="h-3 w-3" /> Platinum
            </span>
          </div>
          <div className="mt-3 h-2 rounded-full bg-[#F3EFFC] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#4B2AAD] to-[#F43F5E]" style={{ width: "81%" }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>2,450 Current Points</span>
            <span>3,000 Points to next tier</span>
          </div>

          <div className="mt-4 rounded-xl bg-[#F5F3FB] p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
            <Benefit icon={Star} label="Earn 5 Points per ₹100" />
            <Benefit icon={Percent} label="Extra 10% off on redemptions" />
            <Benefit icon={ShieldCheck} label="Priority customer support" />
            <Benefit icon={Trophy} label="Early access to offers" />
          </div>
        </div>
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base font-bold text-gray-900">
            Featured Rewards
          </h3>
          <button className="text-xs font-semibold text-[#4B2AAD] hover:underline">
            View all rewards →
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {FEATURED.map((r) => (
            <div
              key={r.code}
              className="rounded-2xl border border-gray-100 bg-white p-4 flex flex-col items-center text-center"
            >
              <div className="h-12 w-12 rounded-2xl bg-[#F3EFFC] flex items-center justify-center">
                <Gift className="h-5 w-5 text-[#4B2AAD]" />
              </div>
              <p className="mt-2 font-display text-lg font-extrabold text-[#4B2AAD]">
                {r.label}
              </p>
              <p className="text-[11px] text-gray-500">{r.desc}</p>
              <p className="mt-3 text-xs text-gray-500">{r.points} Points</p>
              <button
                data-testid={`reward-redeem-${r.code}`}
                className="mt-2 w-full rounded-xl bg-[#4B2AAD] text-white text-xs font-bold py-1.5 hover:bg-[#361D82]"
              >
                Redeem
              </button>
              {r.unlimited && (
                <p className="text-[10px] text-gray-400 mt-1">Unlimited</p>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex gap-1 bg-white border border-gray-100 rounded-2xl p-1 w-fit">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setTab(c)}
              data-testid={`rz-tab-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-1.5 text-xs font-semibold rounded-xl transition-colors ${tab === c
                ? "bg-[#4B2AAD] text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900"
                }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {items.map((v) => (
            <div
              key={v.code}
              className="rounded-2xl border border-gray-100 bg-white p-4"
            >
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl bg-[#F3EFFC] flex items-center justify-center">
                  <Percent className="h-5 w-5 text-[#4B2AAD]" />
                </div>
                <div>
                  <p className="font-display text-base font-bold text-gray-900">
                    {v.label}
                  </p>
                  <p className="text-[11px] text-gray-500">{v.desc}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 text-xs">
                <span className="text-gray-500">Valid for {v.days} days</span>
                <span className="font-bold text-[#4B2AAD]">{v.points} Points</span>
              </div>
              <button className="mt-3 w-full rounded-xl bg-[#4B2AAD] text-white text-xs font-bold py-2 hover:bg-[#361D82]">
                Redeem
              </button>
            </div>
          ))}
          {items.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
              Rewards in this category are coming soon. Check back later!
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base font-bold text-gray-900">
            Redeem Products
          </h3>
          <button className="text-xs font-semibold text-[#4B2AAD] hover:underline">
            View all products →
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {REDEEM_PRODUCTS.map((p) => (
            <div
              key={p.name}
              className="rounded-2xl border border-gray-100 bg-white p-4 flex flex-col"
            >
              <div className="h-24 rounded-xl bg-[#F5F3FB] flex items-center justify-center">
                <Gift className="h-8 w-8 text-[#4B2AAD]" />
              </div>
              <p className="mt-2 text-sm font-bold text-gray-900 line-clamp-2">
                {p.name}
              </p>
              <p className="text-[11px] text-gray-500">{p.pack}</p>
              <p className="text-xs font-bold text-[#4B2AAD] mt-1">{p.points} Points</p>
              <button className="mt-2 w-full rounded-xl bg-[#4B2AAD] text-white text-xs font-bold py-1.5 hover:bg-[#361D82]">
                Redeem
              </button>
            </div>
          ))}
        </div>
      </section>

      <div className="rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/15 p-4 text-xs text-gray-700">
        Points are credited 24–48 hours after order delivery. Some rewards may have
        minimum cart value or product restrictions. <a className="text-[#4B2AAD] font-semibold hover:underline" href="/policy/terms">Terms &amp; Conditions</a>.
      </div>
    </div>
  );
}

function Benefit({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-gray-700">
      <Icon className="h-3.5 w-3.5 text-[#4B2AAD] shrink-0" />
      <span className="text-[11px] font-semibold leading-tight">{label}</span>
    </div>
  );
}
